import { endsWith } from "voca";
import { snakeCase, camelCase, capitalize } from "lodash";
import qs from "qs";
import appConfig from "appConfig";
import { consts as uploaderConsts } from "uploader";
import { getLogger } from "common/logger";
import { localStorage } from "utils/domUtils";
import { api, common as servicesCommon } from "services";
import { openWindow } from "utils/domUtils";
import { MEDIA_TYPES, FILTERS_BY_MEDIA_TYPE, DEFAULT_PAGE_SIZE, INVALID_TOKEN_CODE, CREDIT_PRICES } from "../data/consts";
import type {
	SearchParams
} from "../data/types";

const serverBase = appConfig.iStock.baseUrl;
const { apiSubDomain } = appConfig.iStock;
const { authServer } = appConfig.iStock;
const { integration } = appConfig.iStock;
const { apiKey } = appConfig.iStock;
const { ISTOCK } = uploaderConsts.SOURCES;

const LS_API_DOMAIN_KEY = "UW_iStock_Api";
const LS_INTEGRATION_KEY = "UW_iStock_Integration";

const logger = getLogger();

let authWin = null;

const getBaseUrl = () => {
	const subDomainOverride = localStorage.getItem(LS_API_DOMAIN_KEY);
	const subDomain = subDomainOverride || apiSubDomain;

	return `https://${subDomain}${serverBase}`;
};

const getAuthenticationUrl = () => {
	const integrationOverride = localStorage.getItem(LS_INTEGRATION_KEY);
	return `${authServer}${integrationOverride || integration}/`;
};

const validateToken = (cloudName) => {
	const token = servicesCommon.getStoredAuthToken(cloudName, ISTOCK);

	if (!token) {
		const err: Object = new Error("No token found");
		err.code = INVALID_TOKEN_CODE;
		throw err;
	}
};

const getRequestHeaders = (cloudName: string) => {
	const token = servicesCommon.getStoredAuthToken(cloudName, ISTOCK);
	return token ? {
		"Api-Key": apiKey,
		Authorization: `Bearer ${token}`,
		"Content-Type": "application/json",
	} : {};
};

const getAuthInfo = (cloudName: string): { token: ?string, cloudName: string } => ({
	cloudName,
	token: servicesCommon.getStoredAuthToken(cloudName, ISTOCK),
});


export default () => {
	const baseUrl = getBaseUrl();
	const authUrl = getAuthenticationUrl();

	const openAuthWindow = (config: Config) => {
		const redirectUrl = servicesCommon.getAuthRedirectUrl(config.newTlsDomain);
		const server = config.server || authUrl;

		let loginUrl = `${server}${endsWith(server, "/") ? "" : "/"}`;
		loginUrl = `${loginUrl}${config.cloudName}/login?redirect_url=${redirectUrl}`;

		logger.log(`[iStockService]: created iStock login link: ${loginUrl}`);

		authWin = openWindow(loginUrl, "IStockAuthWin");
	};

	const setAuthListener = (cloudName: string) => servicesCommon.setMessageAuthListener(() => authWin, cloudName, ISTOCK)
	.then((result) => {
		authWin = null;
		return result;
	});

	const logout = (cloudName: string) => {
		servicesCommon.removeAuthToken(cloudName, ISTOCK);
	};
	const FILTERS_EXCLUDED_FROM_QUERY = ["licenseType", "duration"];

	const getSearchQuery = (params: SeachParams, page, mediaType) => {
		const filtersByMediaType = FILTERS_BY_MEDIA_TYPE[mediaType];
		const filters = {};
		if (params?.filters?.duration && filtersByMediaType.includes("duration")) {
			filters["min_clip_length"] = params.filters.duration[0];
			if (params.filters.duration[1] !== 120) {
				filters["max_clip_length"] = params.filters.duration[1];
			}
		}
		params?.filters && Object.keys(params.filters)
			.filter(item => !FILTERS_EXCLUDED_FROM_QUERY.includes(item))
			.filter(item => filtersByMediaType.includes(item))
			.filter(item => !(Array.isArray(params.filters[item]) && !params.filters[item].length))
			.forEach(item => {
				const paramName = snakeCase(item);
				filters[paramName] = Array.isArray(params.filters[item]) ? params.filters[item].join(",") : params.filters[item];
			});

		return {
			phrase: params.searchPhrase ?? "",
			sort_order: params.sortOrder,
			page_size: params.pageSize ?? DEFAULT_PAGE_SIZE,
			page,
			...filters,
			fields: "display_set,detail_set,download_sizes"
		};
	};

	const getSearchApiUrl = (params: SearchParams) => {
		const mediaType = params.mediaType || MEDIA_TYPES.IMAGES;
		let licenseType = "";

		if (params.filters?.licenseType && mediaType === MEDIA_TYPES.IMAGES) {
			licenseType = `/${params.filters.licenseType}`;
		}

		return `${baseUrl}search/${mediaType}${licenseType}`;
	};

	const processResults = (items, mediaType, downloads) => items.map(item => {
		const thumb = item.display_sizes.find(ds => ds.name === "thumb");
		const isVideo = mediaType === MEDIA_TYPES.VIDEOS;
		let preview;
		let link;
		if (isVideo) {
			preview = item.display_sizes.find(ds => ds.name === "preview");
			link = item.display_sizes.find(ds => ds.name === "comp");
		}
		else {
			preview = item.display_sizes.find(ds => ds.name === "preview");
			link = item.display_sizes.find(ds => ds.name === "high_res_comp");
		}
		const purchasedItem = downloads
			.filter((d) => mediaType.includes(d.mediaType))
			.find(d => d.id === item.id);

		return {
			isPurchased: Boolean(purchasedItem?.id),
			productType: purchasedItem?.productType,
			productId: purchasedItem?.productId,
			thumbnail: isVideo ? preview.uri : thumb.uri,
			url: thumb.uri,
			isVideo,
			mediaType,
			allowSelect: true,
			title: item.title,
			id: item.id,
			preview: preview?.uri,
			link: link?.uri || thumb.uri,
			artist: item.artist,
			productTypes: item.product_types,
			creditPrice: CREDIT_PRICES[mediaType][item.collection_code],
			creditsRemaining: item.credits_remaining,
			downloadsRemaining: item.downloads_remaining,
			collectionCode: capitalize(item.collection_code),
			downloadSizes: (item.download_sizes ?? []).map(item => ({
				...item,
				contentType: item?.media_type || item?.content_type
			})),
		};
	});

	const getSearchResults = async (cloudName: string, params: SearchParams, page: number, downloads: Object[]) => {
		const mediaType = params.mediaType || MEDIA_TYPES.IMAGES;
		const query = getSearchQuery(params, page, mediaType);
		const apiURL = getSearchApiUrl(params);

		let response;
		try {
			validateToken(cloudName);
			response = await api.get(apiURL, query, { headers: getRequestHeaders(cloudName) });
		}
		catch (e) {
			return {
				error: true,
				code: e.status ?? e.code
			};
		}
		if (response) {
			return {
				items: processResults(response[mediaType] ?? [], mediaType, downloads),
				total: response.result_count,
				page: page
			};
		}
	};

	const retrieveProducts = async (cloudName: string) => {
		const apiURL = `${baseUrl}products`;
		let response;
		try {
			validateToken(cloudName);
			response = await api.get(apiURL, {fields: "download_requirements"}, { headers: getRequestHeaders(cloudName) });
		}
		catch (e) {
			return {
				error: true
			};
		}

		if (response?.products) {
			return response.products.map(product => {
				const prod = Object.keys(product).reduce((acc, curr) => {
					acc[camelCase(curr)] = product[curr];
					return acc;
				}, {});
				return {
					...prod,
					exceeded: prod.downloadsRemaining === 0 || !prod.creditsRemaining === 0 ,
					value: product.id,
					label: product.name || capitalize(product.type),
				};
			});
		}
	};

	const retrieveDownloads = async (cloudName: string) => {
		const apiURL = `${baseUrl}downloads`;
		const query = { page_size: 100 };
		let response;
		try {
			validateToken(cloudName);
			response = await api.get(apiURL, query, { headers: getRequestHeaders(cloudName) });
		}
		catch (e) {
			return {
				error: true
			};
		}

		return response?.downloads.map(item => ({
			id: item.id,
			mediaType: item.asset_type.toLowerCase(),
			productType: item.product_type,
			productId: item.product_id,
		})) ?? [];
	};

	const purchase = async (asset, format, product, cloudName) => {
		const apiURL = `${baseUrl}downloads/${asset.mediaType}/${asset.id}`;
		let response;
		const query = {
			auto_download: false,
		};
		if (asset.isVideo) {
			query.size = format.value;
			query.product_id = asset.isPurchased ? asset.productId : product.id;
		}
		else {
			query.height = format.height;
			query.file_type = format.value === "vector" ? "eps" : "jpg";
			query.product_type = asset.isPurchased ? asset.productType: product.type;
		}
		try {
			validateToken(cloudName);
			response = await api.post(`${apiURL}?${qs.stringify(query)}`, {}, { headers: getRequestHeaders(cloudName) });
		}
		catch (e) {
			return {
				error: true
			};
		}
		return response;
	};

	const getAccountInfo = async (cloudName) => {
		const apiURL = `${baseUrl}customers/current`;
		let response;
		try {
			validateToken(cloudName);
			response = await api.get(apiURL, {}, { headers: getRequestHeaders(cloudName) });
		}
		catch (e) {
			logger.log("[iStockService]: getting customer data failed");
			return {
				error: true
			};
		}

		return Object.keys(response).reduce((acc, curr) => {
			acc[camelCase(curr)] = response[curr];
			return acc;
		}, {});
	};
	return {
		getAuthInfo,
		getSearchResults,
		setAuthListener,
		openAuthWindow,
		logout,
		retrieveProducts,
		retrieveDownloads,
		purchase,
		validateToken,
		getAccountInfo,
	};
};
